import { getApiBaseUrl } from "../config/runtimeConfig";
import { ApiError } from "../obras/obrasApi";

const apiBaseUrl = getApiBaseUrl();

// Módulo hand-written para as variações de obra (trabalhos a mais/menos) com
// aprovação interna e mapa de variações [4.12.5]/[4.8.1]. Espelha o padrão de
// subcontractorAutos/subcontractorAutosApi.ts: reutiliza o ApiError de obrasApi
// (que humaniza o ProblemDetails do backend) e replica o helper request<>
// mínimo — o request de obrasApi é privado ao módulo, por isso não é importável.
async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

export { ApiError };

export type WorkVariationStatus = "draft" | "approved" | "rejected";
export type WorkVariationKind = "addition" | "reduction";

export interface WorkVariationDto {
  id: number;
  workId: number;
  number: string;
  description: string;
  kind: WorkVariationKind;
  amount: number;
  signedAmount: number;
  status: WorkVariationStatus;
  approvedAt: string | null;
  approvedByUserId: string | null;
  rejectionReason: string | null;
  notes: string | null;
  currencyCode: string;
  created: string;
  lastModified: string;
}

export interface WorkVariationsVm {
  variations: WorkVariationDto[];
  approvedAdditions: number;
  approvedReductions: number;
  netApproved: number;
}

export interface WorkVariationMapDto {
  workId: number;
  workName: string;
  baseSell: number;
  approvedAdditions: number;
  approvedReductions: number;
  revisedSell: number;
  budgetedCost: number;
  actualCost: number;
  baseMargin: number;
  revisedMargin: number;
  revisedMarginPercent: number | null;
  variations: WorkVariationDto[];
}

export interface CreateWorkVariationPayload {
  description: string;
  kind: WorkVariationKind;
  amount: number;
  notes?: string | null;
}

export interface UpdateWorkVariationPayload {
  description: string;
  kind: WorkVariationKind;
  amount: number;
  notes?: string | null;
}

export interface RejectWorkVariationPayload {
  rejectionReason?: string | null;
}

export function getWorkVariations(workId: number): Promise<WorkVariationsVm> {
  return request<WorkVariationsVm>(`/api/works/${workId}/variations`);
}

export function getWorkVariationMap(
  workId: number,
): Promise<WorkVariationMapDto> {
  return request<WorkVariationMapDto>(`/api/works/${workId}/variation-map`);
}

export function createWorkVariation(
  workId: number,
  payload: CreateWorkVariationPayload,
): Promise<WorkVariationDto> {
  return request<WorkVariationDto>(`/api/works/${workId}/variations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateWorkVariation(
  workId: number,
  variationId: number,
  payload: UpdateWorkVariationPayload,
): Promise<WorkVariationDto> {
  return request<WorkVariationDto>(
    `/api/works/${workId}/variations/${variationId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteWorkVariation(
  workId: number,
  variationId: number,
): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/api/works/${workId}/variations/${variationId}`,
    { method: "DELETE", credentials: "include" },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}

export function approveWorkVariation(
  workId: number,
  variationId: number,
): Promise<WorkVariationDto> {
  return request<WorkVariationDto>(
    `/api/works/${workId}/variations/${variationId}/approve`,
    { method: "POST" },
  );
}

export function rejectWorkVariation(
  workId: number,
  variationId: number,
  payload: RejectWorkVariationPayload,
): Promise<WorkVariationDto> {
  return request<WorkVariationDto>(
    `/api/works/${workId}/variations/${variationId}/reject`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function reopenWorkVariation(
  workId: number,
  variationId: number,
): Promise<WorkVariationDto> {
  return request<WorkVariationDto>(
    `/api/works/${workId}/variations/${variationId}/reopen`,
    { method: "POST" },
  );
}
